/**
 * Device check (11/2/2026): the model is a TAP LIST, never free text.
 *
 * One picker for every device add/edit form (field and office). The list is
 * the state (USC) list for the make + type + size, from the same
 * `/api/device-vocabulary` the tech's Device step uses. "Not on this list"
 * still lets someone write down what's really on the wall, but it says so in
 * red: parts can't be ordered for a model that isn't on the list until
 * someone checks it against the tag.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { SearchableCombobox } from "@/components/searchable-combobox";

interface VocabResponse {
    makes: string[];
    sizes: string[];
    models: Array<{ model: string; leadFree: boolean }>;
    listLoaded?: boolean;
}

export function useStateListModels(opts: { manufacturer: string; assemblyType: string; sizeLabel: string }) {
    return useQuery({
        queryKey: ["device-vocabulary", opts.assemblyType, opts.sizeLabel, opts.manufacturer],
        queryFn: () => {
            const params = new URLSearchParams({ assemblyType: opts.assemblyType || "rpz" });
            if (opts.sizeLabel) params.set("size", opts.sizeLabel);
            if (opts.manufacturer) params.set("manufacturer", opts.manufacturer);
            return api.get<VocabResponse>(`/api/device-vocabulary?${params}`);
        },
        enabled: !!opts.manufacturer,
        staleTime: 5 * 60_000,
    });
}

/** True when the list is loaded, a model is set, and the list doesn't carry it. */
export function modelOffList(value: string, models: readonly string[] | undefined, listLoaded: boolean): boolean {
    const v = value.trim().toLowerCase();
    if (!v || !listLoaded || !models || models.length === 0) return false;
    return !models.some((m) => m.toLowerCase() === v);
}

export function NotOnListNote({ show }: { show: boolean }) {
    if (!show) return null;
    return (
        <div className="mt-1 text-xs font-semibold text-red-700 dark:text-red-300" data-testid="model-off-list">
            Not on the state list for this make, type and size. Parts can&apos;t be ordered for it until someone checks
            the tag.
        </div>
    );
}

export function ModelTapList({
    value,
    onChange,
    manufacturer,
    assemblyType,
    sizeLabel,
    id,
}: {
    value: string;
    onChange: (v: string) => void;
    manufacturer: string;
    assemblyType: string;
    sizeLabel: string;
    id?: string;
}) {
    const q = useStateListModels({ manufacturer, assemblyType, sizeLabel });
    const models = (q.data?.models ?? []).map((m) => m.model);
    const listLoaded = q.data?.listLoaded !== false && !!q.data;
    return (
        <div id={id} data-testid="model-tap-list">
            <SearchableCombobox
                value={value}
                onChange={onChange}
                options={models}
                placeholder={manufacturer ? "Tap the model…" : "Pick the make first"}
                disabled={!manufacturer}
                allowCreate
            />
            <NotOnListNote show={modelOffList(value, models, listLoaded)} />
        </div>
    );
}
